import { useState, useEffect, useCallback } from 'react';

interface UseCallTimerReturn {
  duration: number;
  formattedDuration: string;
  start: () => void;
  stop: () => void;
  reset: () => void;
  isRunning: boolean;
}

/**
 * Hook for tracking call duration
 */
export function useCallTimer(startTime?: Date | null): UseCallTimerReturn {
  const [duration, setDuration] = useState(0);
  const [isRunning, setIsRunning] = useState(false);

  /**
   * Format duration as MM:SS
   */
  const formatDuration = useCallback((seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }, []);

  // Convert startTime to timestamp for stable dependency
  const startTimeMs = startTime?.getTime() ?? null;

  // Auto-start/reset timer based on startTime
  useEffect(() => {
    if (startTimeMs === null) {
      // No start time - reset timer
      setDuration(0);
      setIsRunning(false);
      return;
    }

    // Calculate initial elapsed time
    const elapsed = Math.floor((Date.now() - startTimeMs) / 1000);
    setDuration(Math.max(0, elapsed));
    setIsRunning(true);

    // Start interval to increment duration every second
    const interval = setInterval(() => {
      setDuration((prev) => prev + 1);
    }, 1000);

    // Cleanup: clear interval when startTime changes or component unmounts
    return () => {
      clearInterval(interval);
    };
  }, [startTimeMs]);

  /**
   * Manual start - only used if no startTime provided
   */
  const start = useCallback(() => {
    if (isRunning) return;
    setIsRunning(true);
    setDuration(0);
  }, [isRunning]);

  /**
   * Stop the timer
   */
  const stop = useCallback(() => {
    setIsRunning(false);
  }, []);

  /**
   * Reset the timer
   */
  const reset = useCallback(() => {
    setDuration(0);
    setIsRunning(false);
  }, []);

  return {
    duration,
    formattedDuration: formatDuration(duration),
    start,
    stop,
    reset,
    isRunning,
  };
}

export default useCallTimer;
